import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { pageLoading } from "@/app/services/page-loading/pageLoading";
import "./style.css";

/** Show nothing for work that finishes faster than this (avoids flicker on quick calls). */
const LATENCY_THRESHOLD_MS = 100;
const TRICKLE_MS = 250;
const COMPLETE_HOLD_MS = 400;
const FADE_MS = 400;

/** Same easing as angular-loading-bar: quick at first, crawling as it nears the end. */
const trickleIncrement = (progress) => {
  if (progress < 0.25) return 0.03 + Math.random() * 0.03;
  if (progress < 0.65) return 0.03;
  if (progress < 0.9) return 0.02;
  if (progress < 0.99) return 0.005;
  return 0;
};

/**
 * Thin progress bar across the top of the page while the app is working (any non-silent
 * GraphQL request, or an explicit pageLoading.hold()).
 */
export const TopLoadingBar = () => {
  const loading = useSyncExternalStore(pageLoading.subscribe, pageLoading.isLoading, pageLoading.isLoading);
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const visibleRef = useRef(false);
  visibleRef.current = visible;

  useEffect(() => {
    if (loading) {
      let trickle;
      const start = setTimeout(() => {
        setVisible(true);
        setProgress((p) => (p > 0 && p < 1 ? p : 0.02));
        trickle = setInterval(() => setProgress((p) => Math.min(0.99, p + trickleIncrement(p))), TRICKLE_MS);
      }, LATENCY_THRESHOLD_MS);
      return () => {
        clearTimeout(start);
        clearInterval(trickle);
      };
    }
    if (!visibleRef.current) return undefined;
    setProgress(1);
    const hide = setTimeout(() => setVisible(false), COMPLETE_HOLD_MS);
    const reset = setTimeout(() => setProgress(0), COMPLETE_HOLD_MS + FADE_MS);
    return () => {
      clearTimeout(hide);
      clearTimeout(reset);
    };
  }, [loading]);

  return (
    <div
      className={`mg-top-loading-bar${visible ? " mg-top-loading-bar--visible" : ""}`}
      style={{ transform: `scaleX(${progress})` }}
      role="progressbar"
      aria-hidden={!visible}
      aria-busy={loading}
      data-testid="top-loading-bar"
    />
  );
};

export default TopLoadingBar;
